import { formaterDateHeure } from '../../composants/communs.tsx';
import { exporterFacturePdf } from '../../composants/ui/index.ts';
import { libelleMode } from './modes.ts';
import type { DetailVente } from './types.ts';

/**
 * Export PDF de la facture complète — en-tête, lignes, règlements. Les
 * photos du panier n'y figurent pas : ce sont des images, pas du texte à
 * imprimer, et elles restent consultables à l'écran.
 *
 * Partagé entre le détail d'une vente et la Facturation (réimpression sans
 * ouvrir le détail) : une seule mise en page de facture, pas deux qui
 * pourraient diverger.
 */
export function imprimerVente(
  vente: DetailVente,
): Promise<{ blob: Blob; nomFichier: string }> {
  const client =
    vente.client_nom === null || vente.client_nom === undefined
      ? 'Divers'
      : `${vente.client_prenom ?? ''} ${vente.client_nom}`.trim();

  return exporterFacturePdf({
    numero: vente.numero,
    // Adresse/téléphone/email/IFU/RCCM de la boutique n'existent pas encore
    // dans l'API (seul le nom l'est) : la facture les omet proprement
    // plutôt que d'inventer un placeholder — voir exporter-facture-pdf-entete.ts.
    boutique: { nom: vente.boutique },
    dateHeure: formaterDateHeure(vente.horodatage_serveur),
    statut: vente.statut === 'CONTREPASSEE' ? 'Contrepassée' : 'Validée',
    motifContrepassation: vente.motif_contrepassation ?? null,
    vendeur: vente.vendeur,
    client,
    // Pas encore de champ IFU sur la fiche client.
    ifuClient: null,
    canal: vente.canal,
    nomFichier: `vente-${vente.numero}`,
    lignes: vente.lignes.map((ligne) => ({
      designation: ligne.designation,
      reference: ligne.reference,
      quantite: ligne.quantite,
      prixUnitaire: ligne.prix_unitaire,
      remise: ligne.remise_ligne,
      total: ligne.total_ligne,
      imeis: (ligne.imeis ?? [])
        .flatMap((paire) => [paire.imei, paire.imei2])
        .filter((valeur): valeur is string => valeur !== null && valeur !== ''),
    })),
    sousTotal: vente.sous_total,
    remisePied: vente.remise_pied,
    arrondi: vente.arrondi,
    total: vente.total,
    paiements: vente.paiements.map((p) => ({
      mode: libelleMode(p.mode),
      reference:
        p.reference_transaction === null || p.reference_transaction === undefined
          ? '—'
          : `${p.operateur ?? ''} ${p.reference_transaction}`.trim(),
      montant: p.montant,
      dateHeure: formaterDateHeure(p.horodatage_local),
    })),
    encaisse: vente.encaisse,
    resteDu: vente.reste_du,
  });
}
